import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import type { CrmStageNamesResponse, CrmStagesReferenceResponse } from '@aica/api-types';

/**
 * The reference's cache entry — one per organization, shared by every reader.
 * The settings tab's classifying read is a separate entry under the same
 * prefix: invalidating `['crm-stages-reference']` refreshes both. The entry
 * holds the whole answer (`CrmStagesReferenceResponse`): the pipelines and the
 * CRM install they were read from.
 */
export function crmStagesReferenceKey(
  orgId: string | null | undefined,
  classify = false
) {
  return classify
    ? (['crm-stages-reference', orgId, 'classify'] as const)
    : (['crm-stages-reference', orgId] as const);
}

/**
 * The stage names of every active CRM — a separate entry under the same
 * prefix, so invalidating `['crm-stages-reference']` refreshes it too.
 */
export function crmStageNamesKey(orgId: string | null | undefined) {
  return ['crm-stages-reference', orgId, 'names'] as const;
}

interface UseCrmStagesReferenceOptions {
  enabled?: boolean;
  /**
   * The deal stages settings tab only: stages without a cached AI guess get
   * one (a model call on the server). Every other screen — the sales
   * methodology step included (#2491) — leaves it off.
   */
  classify?: boolean;
}

/**
 * Shared query for the org's CRM deal pipelines/stages reference
 * (GET /api/v1/crm/stages). Powers the stage-bindings authoring tab and the
 * stage badge on the analysis surface.
 *
 * Embedded-safe by construction: `apiClient`/`orgId` come from `useAuth()`
 * (UnifiedAuth), which resolves in BOTH the standalone (ClerkAuthProvider) and
 * embedded (EmbeddedAuthProvider) trees. Callers need no mode-specific
 * override args, and there is no `useRole()` here to throw outside
 * OrganizationProvider (the embedded Bitrix24 tree).
 *
 * Contract: the backend returns `{ data: [] }` when no CRM with a pipeline
 * reference is connected, and 503 when the CRM did not answer (#1206 — an
 * outage is never an empty list). `retry: false` avoids retrying a failure
 * three times on every record view. On error `data` stays `undefined` (or
 * keeps the last good list on a failed refetch); callers that only need names
 * treat `data ?? []` as "no reference available" and degrade gracefully (hide
 * names, don't show raw IDs); the stage-bindings tab tells an outage apart.
 */
export function useCrmStagesReference(options: UseCrmStagesReferenceOptions = {}) {
  return useCrmStagesQuery(options, (response) => response.data);
}

/**
 * The CRM install the pipelines of the same cached reference were read from
 * (#2118) — `null` when no CRM pipelines could be listed, `undefined` while
 * unknown or from a server older than the field. The sales methodology
 * step reads and writes for it. Shares the entry (and the request).
 */
export function useCrmStagesReferenceInstallation(
  options: UseCrmStagesReferenceOptions = {}
) {
  return useCrmStagesQuery(options, (response) => response.installationId);
}

/**
 * One query, three views: the cache keeps the whole response, and `select` hands
 * a reader its part. The result is the query's own — never spread, which would
 * read every tracked property and re-render the reader on all of them.
 */
function useCrmStagesQuery<TSelected>(
  options: UseCrmStagesReferenceOptions,
  select: (response: CrmStagesReferenceResponse) => TSelected
) {
  const { enabled = true, classify = false } = options;
  const { apiClient, orgId } = useAuth();

  return useQuery({
    queryKey: crmStagesReferenceKey(orgId, classify),
    queryFn: (): Promise<CrmStagesReferenceResponse> =>
      apiClient.crm.getStages(classify ? { classify } : {}),
    select,
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * The stage names of EVERY active CRM, one list per CRM (GET /crm/stage-names,
 * #2135) — for the screens whose rows come from several CRMs (the «Продажи»
 * stage card, the «Конверсия» funnel). `useCrmStagesReference` describes one
 * install, the first in the registry's order. `data` is undefined when NO CRM
 * answered (503); a partial answer lists the CRMs that did not in `unavailable`.
 * Callers degrade to ids and say so.
 */
export function useCrmStageNames(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { apiClient, orgId } = useAuth();

  return useQuery({
    queryKey: crmStageNamesKey(orgId),
    queryFn: (): Promise<CrmStageNamesResponse> => apiClient.crm.getStageNames(),
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
